import type { Language } from '@osteopro/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { getI18n, NAMESPACES, rememberLanguage, rememberOrgDefaultLanguage } from './config.js';

/**
 * Charge les espaces de noms de l'application avant le premier rendu et tient `<html lang>` à
 * jour : sans lui, les lecteurs d'écran et la césure du navigateur restent en français.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const i18n = getI18n();
  const [ready, setReady] = useState(i18n.isInitialized && i18n.hasLoadedNamespace('common'));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await i18n.loadNamespaces([...NAMESPACES]);
      if (!cancelled) setReady(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [i18n]);

  useEffect(() => {
    const sync = (language: string) => {
      document.documentElement.lang = language;
    };
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => {
      i18n.off('languageChanged', sync);
    };
  }, [i18n]);

  if (!ready) return null;
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

/** Change la langue et la mémorise. Utilisé par le sélecteur de l'en-tête et par les préférences. */
export async function changeLanguage(language: Language): Promise<void> {
  rememberLanguage(language);
  await getI18n().changeLanguage(language);
}

/**
 * Suit la langue du cabinet actif (Profil → défaut de l'organisation). Une bascule ultérieure vers
 * une langue fixe via `changeLanguage` efface ce choix.
 */
export async function changeLanguageToOrgDefault(organizationLanguage: Language): Promise<void> {
  rememberOrgDefaultLanguage();
  await getI18n().changeLanguage(organizationLanguage);
}
